import Phaser from "phaser";
import { EncounterScene } from "../scenes/EncounterScene";
import { Screen, ScreenContext, ScreenKey } from "./types";

export abstract class BaseScreen<
  TScene extends Phaser.Scene = EncounterScene,
  TContext extends ScreenContext = ScreenContext,
> implements Screen<TContext> {
  public readonly key: ScreenKey;
  public readonly container: Phaser.GameObjects.Container;

  protected readonly scene: TScene;

  private trackedTweens: Phaser.Tweens.Tween[] = [];
  private trackedTimers: Phaser.Time.TimerEvent[] = [];
  private trackedCleanup: Array<() => void> = [];

  constructor(scene: TScene, key: ScreenKey) {
    this.scene = scene;
    this.key = key;
    this.container = scene.add.container(0, 0);
  }

  abstract enter(context?: TContext): void;

  exit() {
    this.trackedTweens.forEach((tween) => tween.stop());
    this.trackedTimers.forEach((timer) => timer.remove(false));
    this.trackedCleanup.forEach((cleanup) => cleanup());

    this.trackedTweens = [];
    this.trackedTimers = [];
    this.trackedCleanup = [];

    this.container.destroy(true);
  }

  protected addToContainer<T extends Phaser.GameObjects.GameObject>(object: T): T {
    this.container.add(object);
    return object;
  }

  protected addTween(config: Phaser.Types.Tweens.TweenBuilderConfig): Phaser.Tweens.Tween {
    const tween = this.scene.tweens.add(config);
    this.trackedTweens.push(tween);
    return tween;
  }

  protected delayedCall(
    delay: number,
    callback: () => void,
    args?: unknown[],
    scope?: unknown
  ): Phaser.Time.TimerEvent {
    const timer = this.scene.time.delayedCall(delay, callback, args, scope);
    this.trackedTimers.push(timer);
    return timer;
  }

  protected trackCleanup(cleanup: () => void) {
    this.trackedCleanup.push(cleanup);
  }
}
